import { format } from "date-fns";

import type { LaborVitalsFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/types/labor-vitals-form";
import type { LaborVitalsFlowsheetFormSchemaType } from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/zod-schemas/labor-vitals-flowsheet-form";
import { DEFAULT_FAHRENHEIT_TEMPERATURE_UNIT_SELECT_VALUE } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import { localToUtcTimeString } from "@/utils/timeUtils";

/** Empty hydrate shape (null datetime). Used when editing/mapping a QR — not for a new quick-add form. */
export function getEmptyLaborVitalsFlowsheetFormValues(): LaborVitalsFlowsheetFormValues {
  return {
    vitalDate: null,
    vitalTime: null,
    systolic: "",
    diastolic: "",
    pulse: "",
    temperatureValue: "",
    temperatureUnit: null,
    temperatureMethod: null,
    notes: "",
    signatureInitials: [],
  };
}

/** Defaults for a new quick-add form (current local date/time and Fahrenheit unit prefilled). */
export function getDefaultLaborVitalsFlowsheetFormValues(): LaborVitalsFlowsheetFormValues {
  const now = new Date();
  const localTime = format(now, "HH:mm:ss");
  return {
    ...getEmptyLaborVitalsFlowsheetFormValues(),
    vitalDate: now,
    vitalTime: localToUtcTimeString(localTime),
    temperatureUnit: DEFAULT_FAHRENHEIT_TEMPERATURE_UNIT_SELECT_VALUE,
  };
}

export function normalizeLaborVitalsFlowsheetFormValues(
  parsed: LaborVitalsFlowsheetFormSchemaType,
): LaborVitalsFlowsheetFormValues {
  // Preserve explicit null date/time (cleared or omitted clinical datetime). Do not fall back to
  // "now" via ?? — that belongs only on new-form defaults.
  const empty = getEmptyLaborVitalsFlowsheetFormValues();
  return {
    vitalDate: parsed.vitalDate !== undefined ? parsed.vitalDate : empty.vitalDate,
    vitalTime: parsed.vitalTime !== undefined ? parsed.vitalTime : empty.vitalTime,
    systolic: parsed.systolic ?? empty.systolic,
    diastolic: parsed.diastolic ?? empty.diastolic,
    pulse: parsed.pulse ?? empty.pulse,
    temperatureValue: parsed.temperatureValue ?? empty.temperatureValue,
    temperatureUnit: parsed.temperatureUnit ?? empty.temperatureUnit,
    temperatureMethod: parsed.temperatureMethod ?? empty.temperatureMethod,
    notes: parsed.notes ?? empty.notes,
    signatureInitials: parsed.signatureInitials ?? empty.signatureInitials,
  };
}
